import React, { useEffect, useState } from "react";
import { Truck, X } from "lucide-react";
import i18next from "i18next";
import NativeSafeSelect from "./ui/NativeSafeSelect.jsx";
import VehicleTypePicker from "./vehicle/VehicleTypePicker.jsx";
import { SPECIAL_EQUIPMENT_SUBTYPES, specialEquipmentSubtypeLabel } from "./vehicle/vehicleCatalog.js";

const COPY = {
  ru: { titleCreate: "Свободная машина", titleEdit: "Редактировать машину", subCreate: "Укажите направление и параметры машины. Объявление действует до статуса «Занят»", subEdit: "Измените параметры активной свободной машины", from: "Откуда", to: "Куда", fromPh: "Андижан", toPh: "Ташкент", body: "Кузов", capacity: "Тоннаж", volume: "Объём м³", partial: "Догруз", ttlInfo: "После публикации машина остаётся свободной до статуса «Занят» или ручного завершения.", negotiable: "Договорная", fixed: "Фиксированная", price: "Стоимость", comment: "Комментарий", commentPh: "Дополнительная информация", publish: "Опубликовать машину", publishing: "Публикуем…", save: "Сохранить изменения", saving: "Сохраняем…", routeError: "Укажите откуда и куда.", bodyError: "Укажите тип машины.", capacityError: "Укажите грузоподъёмность.", volumeError: "Проверьте объём машины.", priceError: "Укажите стоимость перевозки.", sessionError: "Не найдена сессия водителя.", limitError: "У вас уже 5 активных машин.", fieldsError: "Проверьте заполненные данные.", inactiveError: "Редактировать можно только активную машину.", notFoundError: "Объявление машины не найдено.", submitError: "Не удалось опубликовать машину." },
  uz: { titleCreate: "Бўш машина", titleEdit: "Машинани таҳрирлаш", subCreate: "Йўналиш ва машина параметрларини киритинг. Эълон «Банд» ҳолатигача амал қилади", subEdit: "Параметрларни ўзгартиринг. Таҳрирлаш муддатни узайтирмайди", from: "Қаердан", to: "Қаерга", fromPh: "Андижон", toPh: "Тошкент", body: "Кузов", capacity: "Тоннаж", volume: "Ҳажм м³", partial: "Қўшимча юк", ttlInfo: "Машина «Банд» ҳолати белгилангунча ёки қўлда якунлангунча бўш қолади.", negotiable: "Нарх келишилади", fixed: "Белгиланган нарх", price: "Нарх", comment: "Изоҳ", commentPh: "Қўшимча маълумот", publish: "Машинани эълон қилиш", publishing: "Эълон қилинмоқда…", save: "Ўзгаришларни сақлаш", saving: "Сақланмоқда…", routeError: "Қаердан ва қаерга эканини киритинг.", bodyError: "Кузов турини кўрсатинг.", capacityError: "Юк кўтариш қувватини киритинг.", volumeError: "Машина ҳажмини текширинг.", priceError: "Ташиш нархини киритинг.", sessionError: "Ҳайдовчи сессияси топилмади.", limitError: "Сизда аллақачон 5 та фаол машина бор.", fieldsError: "Киритилган маълумотларни текширинг.", inactiveError: "Фақат фаол машинани таҳрирлаш мумкин.", notFoundError: "Машина эълони топилмади.", submitError: "Машинани эълон қилиб бўлмади." },
  tr: { titleCreate: "Müsait araç", titleEdit: "Aracı düzenle", subCreate: "Güzergâhı ve araç bilgilerini girin. İlan “Meşgul” durumuna kadar geçerlidir", subEdit: "Bilgileri değiştirin. Düzenleme süreyi uzatmaz", from: "Nereden", to: "Nereye", fromPh: "Andican", toPh: "Taşkent", body: "Kasa tipi", capacity: "Tonaj", volume: "Hacim m³", partial: "Ek yük", ttlInfo: "Araç “Meşgul” yapılana veya ilan manuel kapatılana kadar müsait kalır.", negotiable: "Pazarlık", fixed: "Sabit fiyat", price: "Fiyat", comment: "Açıklama", commentPh: "Ek bilgi", publish: "Aracı yayınla", publishing: "Yayınlanıyor…", save: "Değişiklikleri kaydet", saving: "Kaydediliyor…", routeError: "Nereden ve nereye olduğunu girin.", bodyError: "Kasa tipini seçin.", capacityError: "Taşıma kapasitesini girin.", volumeError: "Araç hacmini kontrol edin.", priceError: "Taşıma fiyatını girin.", sessionError: "Sürücü oturumu bulunamadı.", limitError: "Zaten 5 aktif aracınız var.", fieldsError: "Girilen bilgileri kontrol edin.", inactiveError: "Yalnızca aktif araç düzenlenebilir.", notFoundError: "Araç ilanı bulunamadı.", submitError: "Araç yayınlanamadı." },
};

function languageCode() {
  const raw = String(i18next.language || "ru").toLowerCase();
  if (raw.startsWith("uz")) return "uz";
  if (raw.startsWith("tr")) return "tr";
  return "ru";
}

function bearer(token) {
  const value = String(token || "").trim();
  if (!value) return "";
  return /^bearer\s+/i.test(value) ? value : `Bearer ${value}`;
}


// REISOR_DRIVER_PROFILE_BODY_TO_MARKET_CANON_V1
// Driver profile keeps human-readable body labels.
// Available-vehicle market uses canonical vehicle codes.
function profileBodyTypeToVehicleCode(value) {
  const raw = String(value || "").trim();

  if (!raw) return "";

  const upper = raw.toUpperCase();

  if (
    [
      "TENT",
      "REFRIGERATOR",
      "ISOTHERM",
      "VAN",
      "CONTAINER",
      "FLATBED",
      "OTHER",
    ].includes(upper)
  ) {
    return upper;
  }

  const key = raw.toLocaleLowerCase("ru-RU");

  const map = {
    "тент": "TENT",
    "фургон": "VAN",
    "рефрижератор": "REFRIGERATOR",
    "изотерм": "ISOTHERM",
    "контейнер": "CONTAINER",
    "контейнеровоз": "CONTAINER",
    "платформа": "FLATBED",
    "борт": "FLATBED",
    "бортовой": "FLATBED",
    "платформа/борт": "FLATBED",
    "другое": "OTHER",
  };

  return map[key] || "OTHER";
}


// REISOR_PHASE2B12_BODY_CANON_V1
function phase2B12VehicleCode(value) {
  const raw =
    String(value || "").trim();

  const upper =
    raw.toUpperCase();

  const direct =
    new Set([
      "TENT",
      "REFRIGERATOR",
      "REF",
      "ISOTHERM",
      "VAN",
      "BOX",
      "CONTAINER",
      "FLATBED",
      "PLATFORM",
      "MEGA",
      "PARAVOZ",
      "ISUZU",
      "BONGO",
      "GAZEL",
      "LABO",
      "SAMOSVAL",
      "KAMAZ",
      "CHAKMAN",
      "SPECIAL",
      "OTHER",
    ]);

  if (direct.has(upper)) {
    if (upper === "REF") {
      return "REFRIGERATOR";
    }

    if (upper === "BOX") {
      return "VAN";
    }

    if (upper === "PLATFORM") {
      return "FLATBED";
    }

    return upper;
  }

  const map = {
    "ТЕНТ": "TENT",
    "РЕФРИЖЕРАТОР": "REFRIGERATOR",
    "ИЗОТЕРМ": "ISOTHERM",
    "ФУРГОН": "VAN",

    "КОНТЕЙНЕР": "CONTAINER",
    "КОНТЕЙНЕРОВОЗ": "CONTAINER",

    "ПЛАТФОРМА": "FLATBED",
    "БОРТОВОЙ": "FLATBED",
    "БОРТОВОЙ / ПЛАТФОРМА": "FLATBED",
    "ПЛАТФОРМА/БОРТ": "FLATBED",

    "ФУРА": "PARAVOZ",
    "ПАРОВОЗ": "PARAVOZ",
    "ФУРА / ПАРОВОЗ": "PARAVOZ",

    "ИСУЗУ": "ISUZU",
    "БОНГО": "BONGO",
    "ГАЗЕЛЬ": "GAZEL",
    "ЛАБО": "LABO",

    "САМОСВАЛ": "SAMOSVAL",
    "КАМАЗ": "KAMAZ",
    "ШАКМАН": "CHAKMAN",

    "СПЕЦТЕХНИКА": "SPECIAL",
    "ДРУГОЕ": "OTHER",
  };

  return map[upper] || "OTHER";
}

function freshForm(driver, vehicle = null) {
  return {
    from: String(vehicle?.from || "").trim(),
    to: String(vehicle?.to || "").trim(),
    bodyType:
      phase2B12VehicleCode(
        vehicle?.bodyType ||
        driver?.bodyType ||
        "TENT"
      ),

    bodyTypeOther:
      String(
        vehicle?.bodyTypeOther ||
        driver?.bodyTypeOther ||
        ""
      ).trim(),
    capacityTons:
      vehicle?.capacityTons != null
        ? String(vehicle.capacityTons)
        : driver?.capacityTons != null
          ? String(driver.capacityTons)
          : "",
    volumeM3:
      vehicle?.volumeM3 != null
        ? String(vehicle.volumeM3)
        : driver?.volumeM3 != null
          ? String(driver.volumeM3)
          : "",
    partialLoad: Boolean(vehicle?.partialLoad),
    priceMode:
      String(vehicle?.priceMode || "NEGOTIABLE").toUpperCase() === "FIXED"
        ? "FIXED"
        : "NEGOTIABLE",
    priceAmount:
      vehicle?.priceAmount == null
        ? ""
        : String(vehicle.priceAmount),
    currency:
      String(vehicle?.currency || "UZS").trim().toUpperCase() || "UZS",
    comment: String(vehicle?.comment || ""),
  };
}

export default function DriverAvailableVehicleModal({
  open,
  onClose,
  driverToken,
  driver,
  vehicle = null,
  onCreated,
  onSaved,
}) {
  const lang = languageCode();
  const t = COPY[lang] || COPY.ru;
  const [form, setForm] = useState(() => freshForm(driver, vehicle));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(freshForm(driver, vehicle));
    setError("");
    setBusy(false);
  }, [
    open,
    driver?.bodyType,
    driver?.bodyTypeOther,
    driver?.capacityTons,
    driver?.volumeM3,
    vehicle?.id,
    vehicle?.updatedAt,
  ]);

  const editingId = String(vehicle?.id || "").trim();
  const isEditing = Boolean(editingId);

  if (!open) return null;

  const patch = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;

    setError("");

    const from = String(form.from || "").trim();
    const to = String(form.to || "").trim();
    const bodyType = String(form.bodyType || "").trim().toUpperCase();
    const bodyTypeOther = String(form.bodyTypeOther || "").trim();
    const capacityTons = Number(form.capacityTons);
    const volumeM3 =
      String(form.volumeM3 || "").trim() === "" ? null : Number(form.volumeM3);


    if (!from || !to) return setError(t.routeError);
    if (!bodyType) return setError(t.bodyError);

    if (
      ["SPECIAL", "OTHER"].includes(bodyType) &&
      !bodyTypeOther
    ) {
      return setError(
        lang === "uz"
          ? "Машина / махсус техника турини кўрсатинг."
          : lang === "tr"
            ? "Araç / özel ekipman türünü belirtin."
            : "Укажите тип машины / спецтехники."
      );
    }
    if (!Number.isFinite(capacityTons) || capacityTons <= 0) {
      return setError(t.capacityError);
    }
    if (volumeM3 !== null && (!Number.isFinite(volumeM3) || volumeM3 < 0)) {
      return setError(t.volumeError);
    }

    let priceAmount = null;
    if (form.priceMode === "FIXED") {
      priceAmount = Number(form.priceAmount);
      if (!Number.isFinite(priceAmount) || priceAmount <= 0) {
        return setError(t.priceError);
      }
    }

    const auth = bearer(driverToken);
    if (!auth) return setError(t.sessionError);

    setBusy(true);

    try {
      const endpoint = isEditing
        ? `/api/driver/available-vehicles/${encodeURIComponent(editingId)}`
        : "/api/driver/available-vehicles";

      const res = await fetch(endpoint, {
        method: isEditing ? "PATCH" : "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: auth,
        },
        body: JSON.stringify({
          from,
          to,
          bodyType,
          bodyTypeOther,
          capacityTons,
          volumeM3,
          partialLoad: Boolean(form.partialLoad),
          priceMode: form.priceMode,
          priceAmount,
          currency: form.currency,
          comment: String(form.comment || "").trim(),
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const code = String(data?.error || data?.message || `HTTP ${res.status}`);

        if (code === "AVAILABLE_VEHICLE_ACTIVE_LIMIT_REACHED") {
          throw new Error(t.limitError);
        }
        if (code === "AVAILABLE_VEHICLE_FIELDS_INVALID") {
          throw new Error(t.fieldsError);
        }
        if (code === "AVAILABLE_VEHICLE_NOT_ACTIVE") {
          throw new Error(t.inactiveError);
        }
        if (code === "AVAILABLE_VEHICLE_NOT_FOUND") {
          throw new Error(t.notFoundError);
        }

        throw new Error(code);
      }

      const savedItem = data?.item || null;

      if (isEditing) {
        onSaved?.(savedItem, { mode: "edit" });
      } else {
        onCreated?.(savedItem);
        onSaved?.(savedItem, { mode: "create" });
      }
    } catch (err) {
      setError(String(err?.message || err || t.submitError));
    } finally {
      setBusy(false);
    }
  };

  const input =
    "w-full rounded-2xl border border-slate-700 bg-slate-950/70 px-3 py-3 text-sm text-white outline-none focus:border-blue-400";
  const label =
    "mb-1.5 block text-[14px] font-bold text-slate-400";

  return (
    <div
      className="fixed inset-0 z-[10070] flex items-end bg-black/65 sm:items-center sm:justify-center sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-h-[94dvh] w-full overflow-y-auto rounded-t-3xl border border-slate-700 bg-slate-900 shadow-2xl sm:max-w-xl sm:rounded-3xl"
        data-reisor-theme-surface="driver-available-vehicle">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-700 bg-slate-900/95 px-4 py-4 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600">
              <Truck size={22} />
            </div>
            <div>
              <div className="text-base font-black">
                {isEditing ? t.titleEdit : t.titleCreate}
              </div>
              <div className="text-[14px] text-slate-400">
                {isEditing ? t.subEdit : t.subCreate}
              </div>
            </div>
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-700 bg-slate-800"
          >
            <X size={19} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4 p-4 pb-7">
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className={label}>{t.from}</span>
              <input
                className={input}
                value={form.from}
                onChange={(e) => patch("from", e.target.value)}
                placeholder={t.fromPh}
              />
            </label>

            <label>
              <span className={label}>{t.to}</span>
              <input
                className={input}
                value={form.to}
                onChange={(e) => patch("to", e.target.value)}
                placeholder={t.toPh}
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className={label}>{t.body}</span>
              <VehicleTypePicker
                value={form.bodyType}
                onChange={(value) => {
                  patch(
                    "bodyType",
                    value
                  );

                  if (
                    !["SPECIAL", "OTHER"].includes(
                      String(
                        value || ""
                      ).toUpperCase()
                    )
                  ) {
                    patch(
                      "bodyTypeOther",
                      ""
                    );
                  }
                }}
                domain="vehicle"
              />

              {/* REISOR_PHASE2B12_SUBTYPE_UI_V1 */}
              {["SPECIAL", "OTHER"].includes(
                String(
                  form.bodyType || ""
                ).toUpperCase()
              ) ? (
                <label className="col-span-2">
                  <span className={label}>
                    {String(
                      form.bodyType || ""
                    ).toUpperCase() === "SPECIAL"
                      ? (
                          lang === "uz"
                            ? "Махсус техника тури"
                            : lang === "tr"
                              ? "Özel ekipman türü"
                              : "Тип спецтехники"
                        )
                      : (
                          lang === "uz"
                            ? "Машина турини аниқланг"
                            : lang === "tr"
                              ? "Araç türünü belirtin"
                              : "Уточните тип машины"
                        )}
                  </span>

                  <input
                    className={input}
                    value={
                      form.bodyTypeOther ||
                      ""
                    }
                    onChange={(e) =>
                      patch(
                        "bodyTypeOther",
                        e.target.value
                      )
                    }
                    placeholder={
                      lang === "uz"
                        ? "Экскаватор, автокран, юклагич…"
                        : lang === "tr"
                          ? "Ekskavatör, vinç, yükleyici…"
                          : "Экскаватор, автокран, погрузчик…"
                    }
                  />

                  {String(form.bodyType || "").toUpperCase() === "SPECIAL" ? (
                    <div
                      className="reisor-special-subtype-grid"
                      data-reisor-special-subtype-picker="available-vehicle"
                    >
                      {SPECIAL_EQUIPMENT_SUBTYPES.map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={`reisor-special-subtype-option ${
                            String(form.bodyTypeOther || "") === value
                              ? "is-selected"
                              : ""
                          }`}
                          onClick={() =>
                            patch(
                              "bodyTypeOther",
                              value
                            )
                          }
                        >
                          {specialEquipmentSubtypeLabel(value)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </label>
              ) : null}

            </div>

            <label>
              <span className={label}>{t.capacity}</span>
              <input
                className={input}
                type="number"
                min="0.1"
                step="0.1"
                inputMode="decimal"
                value={form.capacityTons}
                onChange={(e) => patch("capacityTons", e.target.value)}
                placeholder="5"
              />
            </label>

            <label>
              <span className={label}>{t.volume}</span>
              <input
                className={input}
                type="number"
                min="0"
                step="0.1"
                inputMode="decimal"
                value={form.volumeM3}
                onChange={(e) => patch("volumeM3", e.target.value)}
                placeholder="28"
              />
            </label>

            <label className="flex items-end">
              <span className="flex min-h-[46px] w-full items-center gap-2 rounded-2xl border border-slate-700 bg-slate-950/70 px-3">
                <input
                  type="checkbox"
                  checked={form.partialLoad}
                  onChange={(e) => patch("partialLoad", e.target.checked)}
                />
                {t.partial}
              </span>
            </label>
          </div>

          <div className="rounded-2xl border border-blue-500/25 bg-blue-950/25 px-3 py-3 text-sm font-semibold leading-relaxed text-blue-100">
            {t.ttlInfo}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => patch("priceMode", "NEGOTIABLE")}
              className={`rounded-2xl border px-3 py-3 text-sm font-bold ${
                form.priceMode === "NEGOTIABLE"
                  ? "border-blue-400 bg-blue-600/25 text-blue-100"
                  : "border-slate-700 bg-slate-950/60 text-slate-300"
              }`}
            >
              Договорная
            </button>

            <button
              type="button"
              onClick={() => patch("priceMode", "FIXED")}
              className={`rounded-2xl border px-3 py-3 text-sm font-bold ${
                form.priceMode === "FIXED"
                  ? "border-blue-400 bg-blue-600/25 text-blue-100"
                  : "border-slate-700 bg-slate-950/60 text-slate-300"
              }`}
            >
              Фиксированная
            </button>
          </div>

          {form.priceMode === "FIXED" && (
            <div className="grid grid-cols-[1fr_110px] gap-3">
              <input
                className={input}
                type="number"
                min="0"
                value={form.priceAmount}
                onChange={(e) => patch("priceAmount", e.target.value)}
                placeholder={t.price}
              />

              <NativeSafeSelect
                value={form.currency}
                onChange={(value) => patch("currency", value)}
                buttonClassName={input}
                placement="auto"
                ariaLabel="Валюта"
                options={["UZS", "USD", "RUB", "KZT"].map((value) => ({ value, label: value }))}
              />
            </div>
          )}

          <label className="block">
            <span className={label}>{t.comment}</span>
            <textarea
              className={`${input} resize-none`}
              rows={3}
              maxLength={1200}
              value={form.comment}
              onChange={(e) => patch("comment", e.target.value)}
              placeholder={t.commentPh}
            />
          </label>

          {error && (
            <div className="rounded-2xl border border-red-500/35 bg-red-950/35 px-4 py-3 text-sm font-semibold text-red-200">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-2xl bg-blue-600 px-4 py-4 text-base font-black text-white disabled:opacity-60"
          >
            {busy
              ? (isEditing ? "Сохраняем…" : "Публикуем…")
              : (isEditing ? "Сохранить изменения" : "Опубликовать машину")}
          </button>
        </form>
      </div>
    </div>
  );
}
